class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.eventHandlers = new Map();
    this.connectionStatusCallbacks = [];
    this.pingInterval = null;
    this.token = null;
  }

  connect(token) {
    if (this.socket) {
      this.disconnect();
    }

    this.token = token;

    // Build WebSocket URL
    let wsUrl = import.meta.env.VITE_WS_URL;

    if (!wsUrl) {
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const apiHost = apiUrl
        ? apiUrl.replace(/^https?:\/\//, '')
        : window.location.host;
      wsUrl = `${wsProtocol}//${apiHost}/ws/events/`;
    }

    try {
      this.socket = new WebSocket(wsUrl);
      this.setupEventHandlers();
      return this.socket;
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      this.notifyConnectionStatus("error", error.message);
      return null;
    }
  }

  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.socket.send(JSON.stringify({ type: 'authenticate', token: this.token }));
    };

    this.socket.onclose = (event) => {
      this.isConnected = false;
      this.stopPingInterval();
      this.notifyConnectionStatus("disconnected", event.reason);

      // Auto-reconnect unless it was a clean close or authentication error
      if (event.code !== 1000 && event.code !== 4001) {
        this.handleReconnection();
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      this.isConnected = false;
      this.notifyConnectionStatus("error", "Connection error");
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleIncomingMessage(data);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };
  }

  handleIncomingMessage(data) {
    const { type, data: messageData } = data;

    switch (type) {
      case "event":
        this.handleEvent("child_event_created", messageData);
        break;
      case "notification":
        this.handleEvent("notification", messageData);
        break;
      case "user_status":
        this.handleEvent("user_status", messageData);
        break;
      case "conflict":
        this.handleEvent("sync_conflict", messageData);
        break;
      case "queued_event":
        this.handleEvent("queued_event", messageData);
        break;
      case "pong":
        // Handle pong response
        break;
      case "error":
        console.error("WebSocket server error:", messageData);
        this.handleEvent("error", messageData);
        break;
      case "subscription_success":
        this.handleEvent("subscription_success", messageData);
        break;
      case "unsubscription_success":
        this.handleEvent("unsubscription_success", messageData);
        break;
      case "event_marked_read":
        this.handleEvent("event_marked_read", messageData);
        break;
      case "authenticated":
        this.notifyConnectionStatus("connected");
        this.startPingInterval();
        break;
      case "auth_error":
        this.notifyConnectionStatus(
          "error",
          data.message?.reason || "Authentication failed"
        );
        this.socket.close(4001, "Authentication failed");
        break;
      case "sharing_update":
        this.handleEvent("sharing_update", messageData);
        break;
      case "invitation_update":
        this.handleEvent("invitation_update", messageData);
        break;
      case "permission_update":
        this.handleEvent("permission_update", messageData);
        break;
      case "user_activity":
        this.handleEvent("user_activity", messageData);
        break;
      case "share_notification":
        this.handleEvent("share_notification", messageData);
        break;
      default:
        console.warn("Unknown WebSocket message type:", type);
    }
  }

  handleReconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      this.notifyConnectionStatus("max_reconnect_attempts_reached");
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    setTimeout(() => {
      if (!this.isConnected && this.token) {
        this.connect(this.token);
      }
    }, delay);
  }

  handleEvent(eventType, data) {
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error handling ${eventType} event:`, error);
      }
    });
  }

  on(eventType, handler) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType).push(handler);
  }

  off(eventType, handler) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  send(message) {
    if (this.socket && this.isConnected) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn("Cannot send message: WebSocket not connected");
    }
  }

  emit(eventType, data) {
    this.send({ type: eventType, ...data });
  }

  // Convenience methods for common operations
  subscribeToChild(childId) {
    this.send({
      type: "subscribe_child",
      child_id: childId,
    });
  }

  unsubscribeFromChild(childId) {
    this.send({
      type: "unsubscribe_child",
      child_id: childId,
    });
  }

  markEventAsRead(eventId) {
    this.send({
      type: "mark_event_read",
      event_id: eventId,
    });
  }

  ping() {
    this.send({
      type: "ping",
    });
  }

  startPingInterval() {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.ping();
      }
    }, 30000); // Ping every 30 seconds
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  onConnectionStatusChange(callback) {
    this.connectionStatusCallbacks.push(callback);
  }

  offConnectionStatusChange(callback) {
    const index = this.connectionStatusCallbacks.indexOf(callback);
    if (index > -1) {
      this.connectionStatusCallbacks.splice(index, 1);
    }
  }

  notifyConnectionStatus(status, details = null) {
    this.connectionStatusCallbacks.forEach((callback) => {
      try {
        callback(status, details);
      } catch (error) {
        console.error("Error in connection status callback:", error);
      }
    });
  }

  disconnect() {
    this.stopPingInterval();
    if (this.socket) {
      this.socket.close(1000, "Client disconnect");
      this.socket = null;
    }
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.token = null;
    this.eventHandlers.clear();
    this.connectionStatusCallbacks = [];
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      socket: this.socket,
    };
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
